import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { canManageProducts } from "@/lib/permissions";
import { loadCatalogueCleanupJobs, retryCatalogueCleanup } from "@/lib/api/catalogue-cleanup";

export function CatalogueCleanupNotice() {
  const { org, role } = useAuth();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState("");
  const enabled = Boolean(org?.id && canManageProducts(role));
  const query = useQuery({
    queryKey: ["catalogue-cleanup", org?.id],
    queryFn: () => loadCatalogueCleanupJobs(org!.id),
    enabled,
    refetchInterval: 15000,
    retry: 1,
  });
  if (!enabled || (!query.error && !query.data?.length)) return null;
  const jobs = query.data ?? [];
  const attention = jobs.filter((job) => job.status === "blocked" || job.status === "failed");
  const retry = async (jobId: string) => {
    if (!org?.id || pending) return;
    const orgId = org.id;
    setPending(jobId);
    setError("");
    try {
      await retryCatalogueCleanup(orgId, jobId);
      await queryClient.invalidateQueries({ queryKey: ["catalogue-cleanup", orgId] });
    } catch {
      setError("Retry was not accepted. The cleanup record has been retained.");
    } finally {
      setPending(null);
    }
  };
  return (
    <section className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-slate-700" aria-label="Catalogue media cleanup">
      <p className="font-semibold">Catalogue media cleanup</p>
      {query.error ? <p role="alert">Media cleanup status is unavailable. Completion has not been confirmed.</p> : (
        <p>{jobs.length === 100 ? "100 or more" : jobs.length} image cleanup job(s) remain. Catalogue deletion is separate from storage cleanup; jobs continue after you close this page.</p>
      )}
      {attention.length > 0 && (
        <details className="mt-2">
          <summary>{attention.length} job(s) need attention</summary>
          {attention.map((job) => (
            <div key={job.id} className="mt-2 flex flex-wrap items-center gap-2">
              <span>Asset {job.media_asset_id.slice(0, 8)}: {job.status}. {job.last_error ?? "Review required."}</span>
              <button type="button" disabled={pending !== null} onClick={() => void retry(job.id)} className="rounded border border-slate-300 bg-white px-2 py-1 disabled:opacity-50">
                {pending === job.id ? "Requesting..." : "Retry after resolving"}
              </button>
            </div>
          ))}
        </details>
      )}
      {error && <p role="alert" className="mt-2">{error}</p>}
    </section>
  );
}
